// Auto-generated lightweight class for Concern
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { ConcernReadAPI, ConcernWriteAPI, ConcernReadAPISchema, ConcernWriteAPISchema } from '../types/concern';
import { ModelRegistry } from '../../registries/model-registry';
import { ActionVerifiesRequirement, AtScope, ConcernHasStakeholder, Formalises, FramesConcern, HasAttribute, HasAttributeValue, HasDerivedValue, HasRequirement, HasSource, Mentions, NeedTransformedToRequirement, OwnedByUser, RequirementConflictsWith, RequirementDerives, RequirementPart, RequirementRefines, Satisfies } from '../../relationships/relationship';

import { LogicalPart } from './logicalpart';
import { SecurityScope } from '../../shared/api/securityscope';
import { Attribute } from './attribute';
import { AttributeValue } from './attributevalue';
import { DerivedValue } from './derivedvalue';
import { Expression } from './expression';
import { Action } from './action';
import { User } from '../../base/api/user';
import { Requirement } from './requirement';
import { Constraint } from './constraint';
import { SourceDocument } from './sourcedocument';
import { StakeholderNeed } from './stakeholderneed';
import { RFLPElement } from './rflpelement';



export interface Concern extends Partial<ConcernReadAPI> {}
export class Concern extends NeoNodeBase<ConcernReadAPI, ConcernWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "Concern";
  static readonly readSchema = ConcernReadAPISchema;
  static readonly writeSchema = ConcernWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    attributes: { target: "Attribute", cardinality: "many", isParent: false, relClass: "HasAttribute", direction: "out" },
    attribute_values: { target: "AttributeValue", cardinality: "many", isParent: false, relClass: "HasAttributeValue", direction: "out" },
    derived_values: { target: "DerivedValue", cardinality: "many", isParent: false, relClass: "HasDerivedValue", direction: "out" },
    mentioned_in_expressions: { target: "Expression", cardinality: "many", isParent: false, relClass: "Mentions", direction: "in" },
    satisfied_by: { target: "Action", cardinality: "many", isParent: false, relClass: "Satisfies", direction: "in" },
    verified_by_actions: { target: "Action", cardinality: "many", isParent: false, relClass: "ActionVerifiesRequirement", direction: "in" },
    stakeholders: { target: "User", cardinality: "many", isParent: false, relClass: "ConcernHasStakeholder", direction: "out" },
    responsible_user: { target: "User", cardinality: "one", isParent: false, relClass: "OwnedByUser", direction: "out" },
    container: { target: "LogicalPart", cardinality: "one", isParent: true, relClass: "HasRequirement", direction: "in" },
    sub_requirements: { target: "Requirement", cardinality: "many", isParent: false, relClass: "HasRequirement", direction: "out" },
    refines: { target: "Requirement", cardinality: "many", isParent: false, relClass: "RequirementRefines", direction: "out" },
    refined_by: { target: "Requirement", cardinality: "many", isParent: false, relClass: "RequirementRefines", direction: "in" },
    derives_to: { target: "Requirement", cardinality: "many", isParent: false, relClass: "RequirementDerives", direction: "out" },
    derives_from: { target: "Requirement", cardinality: "many", isParent: false, relClass: "RequirementDerives", direction: "in" },
    conflicts_with: { target: "Requirement", cardinality: "many", isParent: false, relClass: "RequirementConflictsWith", direction: "out" },
    conflicts_with_in: { target: "Requirement", cardinality: "many", isParent: false, relClass: "RequirementConflictsWith", direction: "in" },
    constrained_by: { target: "Constraint", cardinality: "many", isParent: false, relClass: "Formalises", direction: "in" },
    sources: { target: "SourceDocument", cardinality: "many", isParent: false, relClass: "HasSource", direction: "out" },
    transformed_from: { target: "StakeholderNeed", cardinality: "many", isParent: false, relClass: "NeedTransformedToRequirement", direction: "in" },
    frames_concerns: { target: "Concern", cardinality: "many", isParent: false, relClass: "FramesConcern", direction: "out" },
    owning_part: { target: "RFLPElement", cardinality: "one", isParent: false, relClass: "RequirementPart", direction: "out" },
    framed_by: { target: "Requirement", cardinality: "many", isParent: false, relClass: "FramesConcern", direction: "in" },
  };

  protected get modelName(): string {
    return 'Concern';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return Concern.label;
  }


  constructor(data: Partial<ConcernWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: ConcernReadAPI, loadContext?: Record<string, any>): Concern {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { container, sub_requirements, refines, refined_by, derives_to, derives_from, conflicts_with, conflicts_with_in, constrained_by, sources, transformed_from, frames_concerns, owning_part, security_scope, attributes, attribute_values, derived_values, mentioned_in_expressions, satisfied_by, verified_by_actions, stakeholders, responsible_user, framed_by, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = ConcernReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, container: container, sub_requirements: sub_requirements, refines: refines, refined_by: refined_by, derives_to: derives_to, derives_from: derives_from, conflicts_with: conflicts_with, conflicts_with_in: conflicts_with_in, constrained_by: constrained_by, sources: sources, transformed_from: transformed_from, frames_concerns: frames_concerns, owning_part: owning_part, security_scope: security_scope, attributes: attributes, attribute_values: attribute_values, derived_values: derived_values, mentioned_in_expressions: mentioned_in_expressions, satisfied_by: satisfied_by, verified_by_actions: verified_by_actions, stakeholders: stakeholders, responsible_user: responsible_user, framed_by: framed_by };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return Concern.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadManyRelationship(processed, 'container', 'LogicalPart', ModelRegistry);
    loadManyRelationship(processed, 'sub_requirements', 'Requirement', ModelRegistry);
    loadManyRelationship(processed, 'refines', 'Requirement', ModelRegistry);
    loadManyRelationship(processed, 'refined_by', 'Requirement', ModelRegistry);
    loadManyRelationship(processed, 'derives_to', 'Requirement', ModelRegistry);
    loadManyRelationship(processed, 'derives_from', 'Requirement', ModelRegistry);
    loadManyRelationship(processed, 'conflicts_with', 'Requirement', ModelRegistry);
    loadManyRelationship(processed, 'conflicts_with_in', 'Requirement', ModelRegistry);
    loadManyRelationship(processed, 'constrained_by', 'Constraint', ModelRegistry);
    loadManyRelationship(processed, 'sources', 'SourceDocument', ModelRegistry);
    loadManyRelationship(processed, 'transformed_from', 'StakeholderNeed', ModelRegistry);
    loadManyRelationship(processed, 'frames_concerns', 'Concern', ModelRegistry);
    loadOneRelationship(processed, 'owning_part', 'RFLPElement', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadManyRelationship(processed, 'attributes', 'Attribute', ModelRegistry);
    loadManyRelationship(processed, 'attribute_values', 'AttributeValue', ModelRegistry);
    loadManyRelationship(processed, 'derived_values', 'DerivedValue', ModelRegistry);
    loadManyRelationship(processed, 'mentioned_in_expressions', 'Expression', ModelRegistry);
    loadManyRelationship(processed, 'satisfied_by', 'Action', ModelRegistry);
    loadManyRelationship(processed, 'verified_by_actions', 'Action', ModelRegistry);
    loadManyRelationship(processed, 'stakeholders', 'User', ModelRegistry);
    loadOneRelationship(processed, 'responsible_user', 'User', ModelRegistry);
    loadManyRelationship(processed, 'framed_by', 'Requirement', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new Concern(processed as any);

    return instance;
  }


  // Relationship manager for 'container' - Python ORM style (cached)
  get container(): RelationshipManager<this, LogicalPart> {
    return this.getOrCreateRelationshipManager('container', () => {
      return new RelationshipManager(this, 'container', 'many', HasRequirement as typeof Relationship);
    });
  }

  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'attributes' - Python ORM style (cached)
  get attributes(): RelationshipManager<this, Attribute> {
    return this.getOrCreateRelationshipManager('attributes', () => {
      return new RelationshipManager(this, 'attributes', 'many', HasAttribute as typeof Relationship);
    });
  }

  // Relationship manager for 'attribute_values' - Python ORM style (cached)
  get attribute_values(): RelationshipManager<this, AttributeValue> {
    return this.getOrCreateRelationshipManager('attribute_values', () => {
      return new RelationshipManager(this, 'attribute_values', 'many', HasAttributeValue as typeof Relationship);
    });
  }

  // Relationship manager for 'derived_values' - Python ORM style (cached)
  get derived_values(): RelationshipManager<this, DerivedValue> {
    return this.getOrCreateRelationshipManager('derived_values', () => {
      return new RelationshipManager(this, 'derived_values', 'many', HasDerivedValue as typeof Relationship);
    });
  }

  // Relationship manager for 'mentioned_in_expressions' - Python ORM style (cached)
  get mentioned_in_expressions(): RelationshipManager<this, Expression> {
    return this.getOrCreateRelationshipManager('mentioned_in_expressions', () => {
      return new RelationshipManager(this, 'mentioned_in_expressions', 'many', Mentions as typeof Relationship);
    });
  }

  // Relationship manager for 'satisfied_by' - Python ORM style (cached)
  get satisfied_by(): RelationshipManager<this, Action> {
    return this.getOrCreateRelationshipManager('satisfied_by', () => {
      return new RelationshipManager(this, 'satisfied_by', 'many', Satisfies as typeof Relationship);
    });
  }

  // Relationship manager for 'verified_by_actions' - Python ORM style (cached)
  get verified_by_actions(): RelationshipManager<this, Action> {
    return this.getOrCreateRelationshipManager('verified_by_actions', () => {
      return new RelationshipManager(this, 'verified_by_actions', 'many', ActionVerifiesRequirement as typeof Relationship);
    });
  }

  // Relationship manager for 'stakeholders' - Python ORM style (cached)
  get stakeholders(): RelationshipManager<this, User> {
    return this.getOrCreateRelationshipManager('stakeholders', () => {
      return new RelationshipManager(this, 'stakeholders', 'many', ConcernHasStakeholder as typeof Relationship);
    });
  }

  // Relationship manager for 'responsible_user' - Python ORM style (cached)
  get responsible_user(): RelationshipManager<this, User> {
    return this.getOrCreateRelationshipManager('responsible_user', () => {
      return new RelationshipManager(this, 'responsible_user', 'one', OwnedByUser as typeof Relationship);
    });
  }

  // Relationship manager for 'sub_requirements' - Python ORM style (cached)
  get sub_requirements(): RelationshipManager<this, Requirement> {
    return this.getOrCreateRelationshipManager('sub_requirements', () => {
      return new RelationshipManager(this, 'sub_requirements', 'many', HasRequirement as typeof Relationship);
    });
  }

  // Relationship manager for 'refines' - Python ORM style (cached)
  get refines(): RelationshipManager<this, Requirement> {
    return this.getOrCreateRelationshipManager('refines', () => {
      return new RelationshipManager(this, 'refines', 'many', RequirementRefines as typeof Relationship);
    });
  }

  // Relationship manager for 'refined_by' - Python ORM style (cached)
  get refined_by(): RelationshipManager<this, Requirement> {
    return this.getOrCreateRelationshipManager('refined_by', () => {
      return new RelationshipManager(this, 'refined_by', 'many', RequirementRefines as typeof Relationship);
    });
  }

  // Relationship manager for 'derives_to' - Python ORM style (cached)
  get derives_to(): RelationshipManager<this, Requirement> {
    return this.getOrCreateRelationshipManager('derives_to', () => {
      return new RelationshipManager(this, 'derives_to', 'many', RequirementDerives as typeof Relationship);
    });
  }

  // Relationship manager for 'derives_from' - Python ORM style (cached)
  get derives_from(): RelationshipManager<this, Requirement> {
    return this.getOrCreateRelationshipManager('derives_from', () => {
      return new RelationshipManager(this, 'derives_from', 'many', RequirementDerives as typeof Relationship);
    });
  }

  // Relationship manager for 'conflicts_with' - Python ORM style (cached)
  get conflicts_with(): RelationshipManager<this, Requirement> {
    return this.getOrCreateRelationshipManager('conflicts_with', () => {
      return new RelationshipManager(this, 'conflicts_with', 'many', RequirementConflictsWith as typeof Relationship);
    });
  }

  // Relationship manager for 'conflicts_with_in' - Python ORM style (cached)
  get conflicts_with_in(): RelationshipManager<this, Requirement> {
    return this.getOrCreateRelationshipManager('conflicts_with_in', () => {
      return new RelationshipManager(this, 'conflicts_with_in', 'many', RequirementConflictsWith as typeof Relationship);
    });
  }

  // Relationship manager for 'constrained_by' - Python ORM style (cached)
  get constrained_by(): RelationshipManager<this, Constraint> {
    return this.getOrCreateRelationshipManager('constrained_by', () => {
      return new RelationshipManager(this, 'constrained_by', 'many', Formalises as typeof Relationship);
    });
  }

  // Relationship manager for 'sources' - Python ORM style (cached)
  get sources(): RelationshipManager<this, SourceDocument> {
    return this.getOrCreateRelationshipManager('sources', () => {
      return new RelationshipManager(this, 'sources', 'many', HasSource as typeof Relationship);
    });
  }

  // Relationship manager for 'transformed_from' - Python ORM style (cached)
  get transformed_from(): RelationshipManager<this, StakeholderNeed> {
    return this.getOrCreateRelationshipManager('transformed_from', () => {
      return new RelationshipManager(this, 'transformed_from', 'many', NeedTransformedToRequirement as typeof Relationship);
    });
  }

  // Relationship manager for 'frames_concerns' - Python ORM style (cached)
  get frames_concerns(): RelationshipManager<this, Concern> {
    return this.getOrCreateRelationshipManager('frames_concerns', () => {
      return new RelationshipManager(this, 'frames_concerns', 'many', FramesConcern as typeof Relationship);
    });
  }

  // Relationship manager for 'owning_part' - Python ORM style (cached)
  get owning_part(): RelationshipManager<this, RFLPElement> {
    return this.getOrCreateRelationshipManager('owning_part', () => {
      return new RelationshipManager(this, 'owning_part', 'one', RequirementPart as typeof Relationship);
    });
  }

  // Relationship manager for 'framed_by' - Python ORM style (cached)
  get framed_by(): RelationshipManager<this, Requirement> {
    return this.getOrCreateRelationshipManager('framed_by', () => {
      return new RelationshipManager(this, 'framed_by', 'many', FramesConcern as typeof Relationship);
    });
  }

}
